import { StyleSheet, css } from 'aphrodite'
import React from 'react'
import { useNavigate } from 'react-router-dom'

interface StakeholderCardSectionProps {
  title?: string
  backgroundImageSrc?: string
  backgroundColor?: string
  titleColor?: string
}

export default function StakeholderCardSection({
  title,
  backgroundImageSrc,
  backgroundColor,
  titleColor,
}: StakeholderCardSectionProps) {
  const navigate = useNavigate()

  const handleSignUpClick = () => {
    window.scrollTo(0, 0)
    navigate('/register')
  }
  const sectionStyles = StyleSheet.create({
    section: {
      padding: '40px 1rem',
      backgroundColor: backgroundColor || 'transparent',
      backgroundImage: backgroundImageSrc
        ? `url(${backgroundImageSrc})`
        : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      '@media (min-width: 768px)': {
        padding: '60px 2rem',
      },
      '@media (min-width: 1024px)': {
        padding: '80px 3rem',
      },
      '@media (min-width: 1280px)': {
        padding: '100px 5rem',
      },
    },
  })

  const titleStyles = StyleSheet.create({
    title: {
      color: titleColor || '#102E65',
    },
  })

  return (
    <section className={css(sectionStyles.section)}>
      {title && (
        <div className={css(styles.tailoredSolutionsHeader)}>
          <h2 className={css(styles.tailoredSolutionsTitle, titleStyles.title)}>
            {title}
          </h2>
        </div>
      )}
      <div className={css(styles.tailoredSolutionsGrid)}>
        <div className={css(styles.tailoredSolutionCard)}>
          <img
            src="/subhauler.webp"
            alt="Sub Hauler Corp."
            className={css(styles.tailoredSolutionImage)}
            loading="lazy"
          />
          <div className={css(styles.tailoredSolutionContent)}>
            <h3 className={css(styles.tailoredSolutionCardTitle)}>
              Sub Hauler Corp.
            </h3>
            <p className={css(styles.tailoredSolutionDescription)}>
              Manage a digital business
            </p>
            <ul className={css(styles.tailoredSolutionFeatures)}>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Scheduling Tags
              </li>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Real-time load information
              </li>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Tailored Invoices from Sub Hauler to Broker
              </li>
            </ul>
            <button
              className={css(styles.tailoredSolutionButton)}
              onClick={handleSignUpClick}
            >
              Sign Up
            </button>
          </div>
        </div>

        <div className={css(styles.tailoredSolutionCard)}>
          <img
            src="/broker.webp"
            alt="Brokers"
            className={css(styles.tailoredSolutionImage)}
            loading="lazy"
          />
          <div className={css(styles.tailoredSolutionContent)}>
            <h3 className={css(styles.tailoredSolutionCardTitle)}>Brokers</h3>
            <p className={css(styles.tailoredSolutionDescription)}>
              Efficient transactions & collaboration
            </p>
            <ul className={css(styles.tailoredSolutionFeatures)}>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Customized dashboards
              </li>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Route optimization tools
              </li>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Eliminate noncompliant invoices
              </li>
            </ul>
            <button
              className={css(styles.tailoredSolutionButton)}
              onClick={handleSignUpClick}
            >
              Sign Up
            </button>
          </div>
        </div>

        <div className={css(styles.tailoredSolutionCard)}>
          <img
            src="/contractors.webp"
            alt="Contractors"
            className={css(styles.tailoredSolutionImage)}
            loading="lazy"
          />
          <div className={css(styles.tailoredSolutionContent)}>
            <h3 className={css(styles.tailoredSolutionCardTitle)}>
              Contractors
            </h3>
            <p className={css(styles.tailoredSolutionDescription)}>
              Eliminate inefficiencies
            </p>
            <ul className={css(styles.tailoredSolutionFeatures)}>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Instant access and real-time insight into the progress and
                status of the hauling project
              </li>
              <li className={css(styles.tailoredSolutionFeature)}>
                <i className={css(styles.tailoredSolutionCheckIcon)} />
                Semi-automated Tags, Status Reports, Project Terms, and
                electronic invoicing and payment-processing
              </li>
            </ul>
            <button
              className={css(styles.tailoredSolutionButton)}
              onClick={handleSignUpClick}
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

const styles = StyleSheet.create({
  tailoredSolutionsHeader: {
    textAlign: 'center',
    marginBottom: '32px',
    '@media (min-width: 768px)': {
      marginBottom: '48px',
    },
    '@media (min-width: 1024px)': {
      marginBottom: '64px',
    },
  },
  tailoredSolutionsTitle: {
    fontSize: '32px',
    fontWeight: 400,
    marginBottom: '16px',
    fontFamily: '"Anton", sans-serif',
    lineHeight: '1.2',
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  tailoredSolutionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '28px',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '32px',
    },
  },
  tailoredSolutionCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    '@media (min-width: 768px)': {
      borderRadius: '10px',
    },
    '@media (min-width: 1024px)': {
      borderRadius: '12px',
    },
  },
  tailoredSolutionImage: {
    width: '100%',
    height: '280px',
    objectFit: 'cover',
    objectPosition: 'top',
    '@media (min-width: 768px)': {
      height: '300px',
    },
    '@media (min-width: 1024px)': {
      height: '450px',
    },
  },
  tailoredSolutionContent: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    '@media (min-width: 768px)': {
      padding: '20px',
    },
    '@media (min-width: 1024px)': {
      padding: '24px',
    },
  },
  tailoredSolutionCardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#102E65',
    marginBottom: '8px',
    '@media (min-width: 768px)': {
      fontSize: '19px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '20px',
    },
  },
  tailoredSolutionDescription: {
    color: '#4b5563',
    marginBottom: '16px',
    lineHeight: '1.5',
    fontWeight: 400,
    fontSize: '14px',
    '@media (min-width: 768px)': {
      fontSize: '15px',
      marginBottom: '20px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '16px',
      marginBottom: '24px',
    },
  },
  tailoredSolutionFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    listStyle: 'none',
    padding: 0,
    margin: '0 0 16px 0',
    '@media (min-width: 768px)': {
      gap: '7px',
      margin: '0 0 20px 0',
    },
    '@media (min-width: 1024px)': {
      gap: '8px',
      margin: '0 0 24px 0',
    },
  },
  tailoredSolutionFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    color: '#374151',
    fontSize: '13px',
    lineHeight: '1.4',
    '::before': {
      content: '"•"',
      fontSize: '14px',
      marginRight: '8px',
      flexShrink: 0,
    },
    '@media (min-width: 768px)': {
      fontSize: '14px',
      '::before': {
        fontSize: '15px',
      },
    },
    '@media (min-width: 1024px)': {
      fontSize: '15px',
      lineHeight: '1.5',
      '::before': {
        fontSize: '16px',
      },
    },
  },
  tailoredSolutionCheckIcon: {
    marginRight: '8px',
    color: '#374151',
  },
  tailoredSolutionButton: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#102E65',
    backgroundColor: '#6DC3FF',
    border: 'none',
    marginTop: 'auto',
    cursor: 'pointer',
    '@media (min-width: 768px)': {
      width: '70%',
      padding: '11px 18px',
      borderRadius: '22px',
      fontSize: '15px',
    },
    '@media (min-width: 1024px)': {
      width: '50%',
      padding: '12px 20px',
      borderRadius: '24px',
      fontSize: '16px',
    },
  },
})
